// crossSellingApi.ts
import axios, { RawAxiosRequestHeaders } from "axios";
import Cookies from "universal-cookie";
import General from "../../../../general";
import config from "../../../../config";

export interface UserAuth {
  userId: string;
  token: string;
}

export interface ApiHeaders {
  apiKey: string;
  userId?: string;
  token?: string;
}

export interface BrandTheme {
  accentColor?: string;
  background?: string;
  borderRadius?: string;
  primaryButton?: string;
  secondaryButton?: string;
  contentColor?: string;
  fontFamily?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  titleColor?: string;
}

export interface QuestThemeData {
  accentColor: string;
  theme: string;
  borderRadius: string;
  buttonColor: string;
  images: string[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface CampaignResponse {
  sdkConfig?: {
    uiProps?: {
      questThemeData?: QuestThemeData;
      BrandTheme?: {
        properties: BrandTheme;
      };
      showFooter?: boolean;
    };
  };
  endsAt: string;
}

interface GetUserAuthParams {
  apiKey: string;
  entityId: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  entityAuthenticationToken?: string;
}

interface ExternalLoginResponse {
  userId: string;
  token: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token: string;
}

class CrossSellingApi {
  private BACKEND_URL: string;
  private GeneralFunctions: General;
  private cookies: Cookies;

  constructor(apiType: string) {
    this.BACKEND_URL =
      apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    this.GeneralFunctions = new General("mixpanel", apiType);
    this.cookies = new Cookies();
  }

  private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
    return {
      "Content-Type": "application/json",
      ...headers,
    };
  }

  async getUserAuth({
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
  }: GetUserAuthParams): Promise<UserAuth | null> {
    try {
      const externalUserId = this.cookies.get("externalUserId");
      const questUserId = this.cookies.get("questUserId");
      const questUserToken = this.cookies.get("questUserToken");

      if (
        externalUserId &&
        questUserId &&
        questUserToken &&
        (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
      ) {
        return {
          userId: questUserId,
          token: questUserToken,
        };
      }

      if (uniqueUserId) {
        const headers = this.getAxiosHeaders({
          apiKey,
          ...(token && { token }),
          ...(userId && { userId }),
        });

        const body = {
          externalUserId: uniqueUserId,
          entityId,
          email: uniqueEmailId || null,
          ignoreMAU: true,
        };

        const response = await axios.post<ApiResponse<ExternalLoginResponse>>(
          `${this.BACKEND_URL}api/users/external/login`,
          body,
          { headers }
        );

        if (!response.data?.success) {
          throw new Error("Invalid response from external login");
        }

        const { userId: newUserId, token: newToken } = response.data;
        const expires = new Date();
        expires.setHours(expires.getHours() + 12);

        this.cookies.set("externalUserId", uniqueUserId, {
          path: "/",
          expires,
        });
        this.cookies.set("questUserId", newUserId, { path: "/", expires });
        this.cookies.set("questUserToken", newToken, { path: "/", expires });

        return {
          userId: newUserId,
          token: newToken,
        };
      }

      if (userId && token) {
        return { userId, token };
      }

      return null;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      console.error(error);
      return null;
    }
  }

  async getCrossSellData(
    entityId: string,
    questId: string,
    headers: ApiHeaders,
    variation?: string
  ) {
    try {
      if (!headers.userId || !headers.token) {
        throw new Error("Missing required headers");
      }

      const params = new URLSearchParams();
      params.set("platform", "REACT");
      if (variation) params.set("variation", variation);

      const axiosHeaders = this.getAxiosHeaders(headers);
      const request = `${
        this.BACKEND_URL
      }api/v2/entities/${entityId}/campaigns/${questId}?${params.toString()}`;

      const response = await axios.get<ApiResponse<CampaignResponse>>(request, {
        headers: axiosHeaders,
      });

      if (!response.data?.success) {
        throw new Error("No data received from API");
      }

      return response.data.data;
    } catch (error) {
      this.GeneralFunctions.captureSentryException(error);
      return null;
    }
  }

  calculateTimeLeft(expiryDate: number): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } {
    const currentTime = Date.now();
    const remainingTime = expiryDate - currentTime;

    if (remainingTime <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }

    return {
      days: Math.floor(remainingTime / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ),
      minutes: Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((remainingTime % (1000 * 60)) / 1000),
    };
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email ? emailRegex.test(email.trim()) : false;
  }
}

export default CrossSellingApi;
